import React from 'react';
import { View, Text } from '@react-pdf/renderer';
import { BillItem } from '../../../pages/hospital/Billing/types';
import { processBillItemsForDisplay } from '../../../utils/billItemsProcessor';
import { commonPDFStyles } from '../styles/pdfStyles';
import { typographyStyles } from '../styles/typography';
import { createT } from '../utils/simpleTranslate';

/**
 * PDF Bill Items Table Component
 * 
 * Renders a table of bill items for PDF documents.
 * Reuses the processing logic from billItemsProcessor utility.
 */

interface PDFBillItemsTableProps {
  items: BillItem[];
  currency: string;
  locale?: string;
}

export const PDFBillItemsTable: React.FC<PDFBillItemsTableProps> = ({
  items,
  currency,
  locale = 'fr',
}) => {
  const t = createT(locale);
  
  // Process items using the shared utility
  const processedItems = processBillItemsForDisplay(items, currency, locale, true);

  // Table styles
  const tableStyles = {
    table: {
      width: '100%',
      marginTop: 10,
      marginBottom: 0,
    },
    headerRow: {
      flexDirection: 'row' as const,
      borderBottomWidth: 1,
      borderBottomColor: '#000',
      borderBottomStyle: 'solid' as const,
      paddingBottom: 4,
      marginBottom: 4,
    },
    headerCell: {
      ...typographyStyles.body2,
      fontWeight: 'bold',
    },
    row: {
      flexDirection: 'row' as const,
      paddingVertical: 3,
      borderBottomWidth: 0.5,
      borderBottomColor: '#E0E0E0',
      borderBottomStyle: 'solid' as const,
    },
    totalRow: {
      flexDirection: 'row' as const,
      paddingVertical: 4,
      marginTop: 4,
      borderTopWidth: 1,
      borderTopColor: '#000',
      borderTopStyle: 'solid' as const,
    },
    summaryRow: {
      flexDirection: 'row' as const,
      paddingVertical: 3,
    },
    // Column widths
    conceptCell: {
      width: '45%',
      paddingRight: 5,
    },
    quantityCell: {
      width: '15%',
      textAlign: 'center' as const,
    },
    amountCell: {
      width: '20%',
      textAlign: 'right' as const,
    },
    subtotalCell: {
      width: '20%',
      textAlign: 'right' as const,
    },
  };

  return (
    <View style={tableStyles.table}>
      {/* Table Header */}
      <View style={tableStyles.headerRow}>
        <Text style={[tableStyles.headerCell, tableStyles.conceptCell]}>
          {t('hospital.billing.item.concept')}
        </Text>
        <Text style={[tableStyles.headerCell, tableStyles.quantityCell]}>
          {t('hospital.billing.item.quantity')}
        </Text>
        <Text style={[tableStyles.headerCell, tableStyles.amountCell]}>
          {t('hospital.billing.item.unitCost')}
        </Text>
        <Text style={[tableStyles.headerCell, tableStyles.subtotalCell]}>
          {t('hospital.billing.item.subtotal')}
        </Text>
      </View>

      {/* Table Body */}
      {processedItems.map((item) => {
        const rowStyle = item.isTotal 
          ? tableStyles.totalRow 
          : item.isSummary 
          ? tableStyles.summaryRow 
          : tableStyles.row;
        
        const textStyle = {
          ...typographyStyles.body2,
          color: item.color,
          ...(item.isBold && { fontWeight: 'bold', fontSize: 11 }),
        };

        // For total and summary rows, span the concept across multiple columns
        if (item.isTotal || item.isSummary) {
          return (
            <View key={item.id} style={rowStyle}>
              <Text style={[textStyle, { width: '80%', textAlign: 'right', paddingRight: 5 }]}>
                {item.concept === 'Total' 
                  ? t('hospital.billing.bill.total')
                  : item.concept === 'Insurance Coverage'
                  ? t('hospital.billing.bill.insurance_coverage')
                  : item.concept === 'Patient Responsibility'
                  ? t('hospital.billing.bill.patient_responsability')
                  : item.concept === 'Remaining Amount'
                  ? t('hospital.billing.bill.remaining_amount')
                  : item.concept
                }:
              </Text>
              <Text style={[textStyle, tableStyles.subtotalCell]}>
                {item.subtotal}
              </Text>
            </View>
          );
        }

        // Regular item row
        return (
          <View key={item.id} style={rowStyle}>
            <Text style={[textStyle, tableStyles.conceptCell]}>
              {item.concept}
            </Text>
            <Text style={[textStyle, tableStyles.quantityCell]}>
              {item.quantity}
            </Text>
            <Text style={[textStyle, tableStyles.amountCell]}>
              {item.amount}
            </Text>
            <Text style={[textStyle, tableStyles.subtotalCell]}>
              {item.subtotal}
            </Text>
          </View>
        );
      })}
    </View>
  );
};

export default PDFBillItemsTable;

